import { useQueries, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { useState } from "react";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { ResultsCapError } from "@/utilities/error/ResultsCapError";
import { createConcurrencyLimiter } from "@/utilities/query/createConcurrencyLimiter";

interface Args<T> {
  /** First key segment; the session, device and period are appended. */
  queryKey: string;
  deviceIds: string[];
  fromDate: Date;
  toDate: Date;
  enabled?: boolean;
  /**
   * Most devices fetched at once; the rest wait. Unset means all at once. Set it when one device
   * costs several calls, so a large fleet does not queue hundreds of calls in one go.
   */
  maxConcurrent?: number;
  /** Loads the records of one device; called once per device so rows can appear as each finishes. */
  fetchRecords: (deviceId: string, signal?: AbortSignal) => Promise<T[]>;
}

type DeviceRecords<T> = { deviceId: string; records: T[] };

// Module-level so TanStack sees a stable reference and does not re-run it on every render.
function combineByDevice<T>(results: UseQueryResult<DeviceRecords<T>>[]) {
  const byDevice: Record<string, T[]> = {};
  let loadedCount = 0;
  for (const result of results) {
    if (result.data) {
      byDevice[result.data.deviceId] = result.data.records;
      loadedCount++;
    }
  }
  return {
    byDevice,
    /** Devices whose records have arrived; with `totalCount` it drives a progress display. */
    loadedCount,
    failedCount: results.filter(r => r.isError).length,
    totalCount: results.length,
    isLoading: results.some(r => r.isLoading),
    isFetching: results.some(r => r.isFetching),
    isPending: results.some(r => r.isPending),
    isError: results.some(r => r.isError),
    error: results.find(r => r.error)?.error ?? null,
    /** Devices whose query has finished, successfully or not; with `totalCount` for a progress display. */
    read: results.filter(r => !r.isPending).length,
    /** A failed device is being fetched again; TanStack keeps it in `error` until it settles. */
    isRetrying: results.some(r => r.isError && r.isFetching),
    /** Positions (in `deviceIds`) of the devices whose query failed. */
    failedIndexes: results.flatMap((r, i) => (r.isError ? [i] : [])),
  };
}

/** One query per device for a period, combined into records by device id. */
export function usePerDeviceQueries<T>({
  queryKey,
  deviceIds,
  fromDate,
  toDate,
  enabled = true,
  maxConcurrent,
  fetchRecords,
}: Args<T>) {
  const { activeSessionId } = useGeotabClient();
  const queryClient = useQueryClient();
  const [limit] = useState(() =>
    maxConcurrent === undefined ? undefined : createConcurrencyLimiter(maxConcurrent),
  );

  const combined = useQueries({
    queries: deviceIds.map(deviceId => ({
      queryKey: [queryKey, activeSessionId, deviceId, fromDate, toDate],
      enabled,
      // A full page is deterministic; retrying would only download the same records again.
      retry: (failureCount: number, error: unknown) =>
        !(error instanceof ResultsCapError) && failureCount < 3,
      queryFn: ({ signal }: { signal?: AbortSignal }) => {
        const load = () =>
          fetchRecords(deviceId, signal).then(records => ({ deviceId, records }));
        return limit ? limit(load, signal) : load();
      },
    })),
    combine: combineByDevice<T>,
  });

  const { failedIndexes, ...rest } = combined;
  return {
    ...rest,
    /** Devices whose query failed; their records (even earlier ones) must not count as complete. */
    failedDeviceIds: failedIndexes.map(i => deviceIds[i]),
    /**
     * Refetches only the devices whose query failed; the loaded ones stay as they are.
     * Only active queries: failed ones of an earlier period still in the cache are left alone.
     * Devices that hit the result limit are skipped: they would hit it again.
     */
    retryFailed: () =>
      queryClient.refetchQueries({
        queryKey: [queryKey, activeSessionId],
        type: "active",
        predicate: query =>
          query.state.status === "error" && !(query.state.error instanceof ResultsCapError),
      }),
  };
}
